import {groupBy} from 'remeda'

import Checkbox from '@/components/Checkbox/Checkbox'
import CopyableText from '@/components/CopyableText'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import ProductRetailerImage from '@/components/ProductCard/ProductRetailerImage'
import {useCurrencySymbol} from '@/lib/locale/country'
import {type Order, type OrderItem, OrderItemStatus} from '@/models/orderModel'
import {ProductRoute} from '@/routeRegistry'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import BuyAgainDialog from '@/views/Account/Orders/components/BuyAgainDialog'

import style from './OrderItemGroups.module.scss'

interface ItemProps {
  item: OrderItem
  isSelectable?: boolean
  onSelect?: (item: OrderItem, state: boolean) => void
  isDisabled?: boolean
  checkboxLabel?: string
  showPrice?: boolean
  showQuantity?: boolean
  showTrackingInfo?: boolean
  showVariation?: boolean
  showBuyAgain?: boolean
  order?: Order
  onOpenChange?: (isOpen: boolean) => void
}

const ORDER_ITEM_STATUS_LABELS: Record<OrderItemStatus, string> = {
  [OrderItemStatus.SHIPPED]: 'Shipped',
  [OrderItemStatus.OUT_FOR_DELIVERY]: 'Out for delivery',
  [OrderItemStatus.DELIVERED]: 'Delivered',
  [OrderItemStatus.RETURN_REQUESTED]: 'Return requested',
  [OrderItemStatus.RETURNED]: 'Returned',
  [OrderItemStatus.REFUNDED]: 'Refunded',
  [OrderItemStatus.COMPLETED]: 'Completed',
  [OrderItemStatus.CONFIRMED]: 'Confirmed',
}

const OrderItem = memo<ItemProps>(function OrderItem({
  item,
  isSelectable,
  onSelect,
  isDisabled,
  checkboxLabel,
  showPrice = true,
  showQuantity = true,
  showTrackingInfo = true,
  showVariation = true,
  showBuyAgain = false,
  order,
  onOpenChange,
}) {
  const currencySymbol = useCurrencySymbol()

  const onChange = useCallback(
    (value: boolean) => {
      onSelect?.(item, value)
    },
    [item, onSelect],
  )

  return (
    <div
      className={clsx(style.Item, isDisabled && style.IsDisabled)}
      key={`${item.retailer}-${item.id}`}
    >
      {isSelectable && (
        <div className={style.Selection}>
          <Checkbox
            label={`${checkboxLabel} "${item.title}"`}
            onChange={onChange}
            isReadOnly={isDisabled}
          />
          {isDisabled && <VisuallyHidden>Not available for {checkboxLabel}</VisuallyHidden>}
        </div>
      )}
      <div className={style.ItemImage}>
        <img src={item.image} alt='' aria-hidden='true' loading='lazy' />
      </div>
      <div className={style.ItemInfo}>
        <h3 className={style.ItemTitle}>
          <Link
            to={ProductRoute.fullPath}
            params={{
              productId: {
                retailer: item.retailer,
                id: item.id,
              },
            }}
            className={style.ItemTitle}
          >
            {item.title}
          </Link>
        </h3>
        {/* TODO: add support for variations update */}
        {showVariation && item.variation && (
          <div className={style.ItemOptions}>
            {Object.entries(item.variation)
              .map(([key, value]) => `${key.slice(0, 1).toUpperCase() + key.slice(1)}: ${value}`)
              .join(' | ')}
          </div>
        )}
        {showPrice && (
          <div className={style.ItemPrice}>
            <VisuallyHidden>Price: </VisuallyHidden>
            {currencySymbol}
            {item.price}
          </div>
        )}
      </div>
      {showBuyAgain && order && (
        <div className={style.BuyAgainContainer}>
          <BuyAgainDialog
            order={order}
            preSelectedItemKey={`${item.retailer}-${item.id}`}
            onOpenChange={onOpenChange}
          >
            <PrimaryButton size='sm' className={style.BuyAgainButton}>
              Buy Again
            </PrimaryButton>
          </BuyAgainDialog>
        </div>
      )}
      {showQuantity && (
        <div className={style.ItemQuantity}>
          <VisuallyHidden>Quantity: </VisuallyHidden>
          {item.quantity}
        </div>
      )}
      {showTrackingInfo && (
        <div className={style.TrackingInfo}>
          <div className={style.ItemEstimatedDeliveryDate}>
            {/* {deliveryDatePrefix}&nbsp; */}
            {item.estimatedDeliveryDate}
          </div>
          <div className={style.ItemStatusAndCarrier}>
            <div className={style.ItemStatus}>
              {item.status ? ORDER_ITEM_STATUS_LABELS[item.status] : 'Confirmed'}
            </div>
            {item.carrier && <div className={style.ItemCarrier}> by {item.carrier}</div>}
          </div>
          {item.shippingTrackingNumber && (
            <CopyableText
              className={style.ItemTrackingNumber}
              text={item.shippingTrackingNumber}
              label='tracking number'
              iconProps={{
                className: style.ItemTrackingNumberIcon,
              }}
            >
              <a
                href={`https://www.aftership.com/track/${item.shippingTrackingNumber}`}
                target='_blank'
                rel='noopener noreferrer'
              >
                {item.shippingTrackingNumber}
              </a>
            </CopyableText>
          )}
        </div>
      )}
    </div>
  )
})

interface Props {
  items: OrderItem[]
  isSelectable?: boolean
  isItemDisabled?: (item: OrderItem) => boolean
  onSelect?: (item: OrderItem, state: boolean) => void
  checkboxLabel?: string
  showRetailer?: boolean
  showPrice?: boolean
  showQuantity?: boolean
  showTrackingInfo?: boolean
  showVariation?: boolean
  showBuyAgain?: boolean
  order?: Order
  onOpenChange?: (isOpen: boolean) => void
}

export default memo<Props>(function OrderItemGroups({
  items,
  isSelectable,
  isItemDisabled,
  onSelect,
  checkboxLabel,
  showRetailer,
  showPrice = true,
  showQuantity = true,
  showTrackingInfo = true,
  showVariation = true,
  showBuyAgain = false,
  order,
  onOpenChange,
}) {
  const groupedItems = groupBy(items, item => item.status ?? '')

  return (
    <div className={style.ItemGroups} role='region' aria-label='Order items'>
      {Object.entries(groupedItems).map(([, items]) =>
        items.map(item => (
          <div key={`${item.retailer}-${item.id}`}>
            <div className={style.ItemRetailer}>
              {showRetailer && (
                <ProductRetailerImage retailer={item.seller ?? item.retailer} shouldDisplayText />
              )}
            </div>
            <OrderItem
              key={`${item.retailer}-${item.id}`}
              item={item}
              isSelectable={isSelectable}
              onSelect={onSelect}
              isDisabled={isItemDisabled?.(item) ?? false}
              checkboxLabel={checkboxLabel}
              showPrice={showPrice}
              showQuantity={showQuantity}
              showTrackingInfo={showTrackingInfo}
              showVariation={showVariation}
              showBuyAgain={showBuyAgain}
              order={order}
              onOpenChange={onOpenChange}
            />
          </div>
        )),
      )}
    </div>
  )
})
